import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const lines = ["ELEVATE", "YOUR", "LIFESTYLE"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 1,
      delay: 0.4 + i * 0.14,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-32 md:px-10"
    >
      {/* Ambient parallax glow */}
      <motion.div
        style={{ y }}
        aria-hidden
        className="pointer-events-none absolute -top-20 right-[-10%] h-[520px] w-[520px] rounded-full bg-[#ccff00] opacity-[0.07] blur-[130px]"
      />
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -120]) }}
        aria-hidden
        className="pointer-events-none absolute bottom-10 left-[-8%] h-[360px] w-[360px] rounded-full bg-emerald-500 opacity-[0.06] blur-[120px]"
      />

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-7xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 flex items-center gap-3 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500"
        >
          <span className="h-px w-10 bg-[#ccff00]" />
          Curated referral drops — 3 brands, one code
        </motion.div>

        <h1 className="font-display text-[15vw] font-extrabold leading-[0.85] tracking-tighter text-white md:text-[11vw]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.5vw]">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line === "YOUR" ? (
                  <span className="text-outline">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-base leading-relaxed text-zinc-400 md:text-lg"
          >
            A hand-picked lineup of the products I actually use — energy, flower,
            and hardware. Tap any drop below and stack an instant{" "}
            <span className="text-[#ccff00]">10% discount</span> at checkout.
          </motion.p>

          <motion.a
            href="#drops"
            data-testid="hero-explore-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="group flex items-center gap-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-white"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/20 transition-colors duration-300 group-hover:border-[#ccff00] group-hover:bg-[#ccff00]">
              <ArrowDown className="h-5 w-5 transition-colors duration-300 group-hover:text-black" />
            </span>
            Explore the drops
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};
